import { Controller } from '@hotwired/stimulus'

const RESIZE_SETTLE_DELAY_MS = 120
const SIDEBAR_TRANSITION_BUFFER_MS = 50
const STREAM_MIN_SCROLL_ADJUSTMENT_PX = 2

export default class extends Controller {
  connect() {
    this.streamSnapshot = null
    this.streamRestoreFrame = null
    this.streamRestoreGuardFrame = null
    this.streamRestoreVersion = 0
    this.streamSnapshotCapturedAt = 0
    this.lastStreamScrollAt = 0
    this.lastStreamUserIntentAt = 0
    this.isRestoringStreamScroll = false
    this.streamScrollContainers = []
    this.streamIntentContainers = []

    this.resizeSnapshot = null
    this.resizeRestoreFrame = null
    this.resizeReleaseFrame = null
    this.resizeSettleTimer = null

    this.sidebarSnapshot = null
    this.sidebarRestoreFrame = null
    this.sidebarReleaseFrame = null
    this.sidebarTransitionElement = null
    this.sidebarTransitionHandler = null
    this.sidebarTransitionTimeout = null

    this.bindStreamScrollListeners()
    document.addEventListener('turbo:before-stream-render', this.preserveStreamScrollPosition)
    window.addEventListener('resize', this.handleResize)
    window.visualViewport?.addEventListener('resize', this.handleResize)
    window.addEventListener('galette:sidebar:before-open', this.handleSidebarLayoutChange)
    window.addEventListener('galette:sidebar:before-close', this.handleSidebarLayoutChange)
  }

  disconnect() {
    this.unbindStreamScrollListeners()
    this.unbindStreamIntentListeners()

    document.removeEventListener('turbo:before-stream-render', this.preserveStreamScrollPosition)
    window.removeEventListener('resize', this.handleResize)
    window.visualViewport?.removeEventListener('resize', this.handleResize)
    window.removeEventListener('galette:sidebar:before-open', this.handleSidebarLayoutChange)
    window.removeEventListener('galette:sidebar:before-close', this.handleSidebarLayoutChange)

    this.cancelFrame(this.streamRestoreFrame)
    this.cancelFrame(this.streamRestoreGuardFrame)
    this.cancelFrame(this.resizeRestoreFrame)
    this.cancelFrame(this.resizeReleaseFrame)
    this.cancelFrame(this.sidebarRestoreFrame)
    this.cancelFrame(this.sidebarReleaseFrame)
    clearTimeout(this.resizeSettleTimer)
    clearTimeout(this.sidebarTransitionTimeout)

    this.isRestoringStreamScroll = false
    this.releaseStreamSnapshot()
    this.releaseResizeSnapshot()
    this.releaseSidebarSnapshot()
    this.teardownSidebarTransitionListener()
  }

  cancelFrame = frame => {
    if (frame) cancelAnimationFrame(frame)
  }

  bindStreamScrollListeners = () => {
    const containers = this.getScrollContainers()
    this.streamScrollContainers = [...new Set([containers?.scrollContainer, containers?.formScrollContainer].filter(Boolean))]

    this.streamScrollContainers.forEach(element => {
      element.addEventListener('scroll', this.handleStreamContainerScroll, { passive: true })
    })
  }

  unbindStreamScrollListeners = () => {
    this.streamScrollContainers.forEach(element => {
      element.removeEventListener('scroll', this.handleStreamContainerScroll)
    })

    this.streamScrollContainers = []
  }

  bindStreamIntentListeners = () => {
    const containers = this.getScrollContainers()
    this.streamIntentContainers = [...new Set([containers?.scrollContainer, containers?.formScrollContainer].filter(Boolean))]

    this.streamIntentContainers.forEach(element => {
      element.addEventListener('wheel', this.handleStreamUserIntent, { passive: true })
      element.addEventListener('touchmove', this.handleStreamUserIntent, { passive: true })
      element.addEventListener('pointerdown', this.handleStreamUserIntent, { passive: true })
    })
  }

  unbindStreamIntentListeners = () => {
    this.streamIntentContainers.forEach(element => {
      element.removeEventListener('wheel', this.handleStreamUserIntent)
      element.removeEventListener('touchmove', this.handleStreamUserIntent)
      element.removeEventListener('pointerdown', this.handleStreamUserIntent)
    })

    this.streamIntentContainers = []
  }

  getScrollContainers = () => {
    const scrollContainer = document.getElementById('sidebar-scroll-container')
    if (!scrollContainer) return null

    return {
      scrollContainer,
      formScrollContainer: document.getElementById('super-form-scroll-container')
    }
  }

  getSidebarElement = event => {
    return event?.detail?.sidebar || document.getElementById('app-sidebar')
  }

  isScrollable = element => {
    if (!element) return false

    return element.scrollHeight > element.clientHeight + 1
  }

  getActiveResizeScrollContainer = () => {
    const containers = this.getScrollContainers()
    if (!containers) return null

    if (this.isScrollable(containers.formScrollContainer)) {
      return containers.formScrollContainer
    }

    return containers.scrollContainer
  }

  createAnchoringSnapshot = containers => {
    return containers
      .filter(Boolean)
      .map(element => {
        this.lockOverflowAnchoring(element)
        return element
      })
  }

  lockOverflowAnchoring = element => {
    const lockCount = Number(element.dataset.scrollPreservationLockCount || 0)

    if (lockCount === 0) {
      element.dataset.scrollPreservationPreviousOverflowAnchor = element.style.overflowAnchor
      element.style.overflowAnchor = 'none'
    }

    element.dataset.scrollPreservationLockCount = String(lockCount + 1)
  }

  unlockOverflowAnchoring = element => {
    const lockCount = Number(element.dataset.scrollPreservationLockCount || 0)

    if (lockCount <= 1) {
      element.style.overflowAnchor = element.dataset.scrollPreservationPreviousOverflowAnchor || ''
      delete element.dataset.scrollPreservationLockCount
      delete element.dataset.scrollPreservationPreviousOverflowAnchor
      return
    }

    element.dataset.scrollPreservationLockCount = String(lockCount - 1)
  }

  captureStreamSnapshot = () => {
    const containers = this.getScrollContainers()
    if (!containers) return null

    this.streamSnapshotCapturedAt = performance.now()

    return {
      ...containers,
      scrollTop: containers.scrollContainer.scrollTop,
      formScrollTop: containers.formScrollContainer?.scrollTop,
      anchoredContainers: this.createAnchoringSnapshot([containers.scrollContainer, containers.formScrollContainer])
    }
  }

  markStreamUserIntent = () => {
    this.lastStreamUserIntentAt = performance.now()
  }

  snapshotIsStale = () => {
    if (!this.streamSnapshot) return false

    return this.lastStreamScrollAt > this.streamSnapshotCapturedAt
  }

  snapshotIsTrustworthyForFollowUp = restoreVersion => {
    if (!this.streamSnapshot) return false
    if (restoreVersion !== this.streamRestoreVersion) return false
    if (this.snapshotIsStale()) return false
    if (this.lastStreamUserIntentAt > this.streamSnapshotCapturedAt) return false

    return true
  }

  guardProgrammaticStreamScroll = callback => {
    this.isRestoringStreamScroll = true
    callback()

    this.cancelFrame(this.streamRestoreGuardFrame)
    this.streamRestoreGuardFrame = requestAnimationFrame(() => {
      this.isRestoringStreamScroll = false
      this.streamRestoreGuardFrame = null
    })
  }

  restoreScrollContainerTo = (element, targetScrollTop) => {
    if (!element || targetScrollTop === undefined) return

    const delta = element.scrollTop - targetScrollTop
    if (Math.abs(delta) < STREAM_MIN_SCROLL_ADJUSTMENT_PX) return

    element.scrollTop = targetScrollTop
  }

  restoreStreamSnapshot = () => {
    if (!this.streamSnapshot) return

    const { scrollContainer, scrollTop, formScrollContainer, formScrollTop } = this.streamSnapshot

    this.guardProgrammaticStreamScroll(() => {
      this.restoreScrollContainerTo(scrollContainer, scrollTop)
      this.restoreScrollContainerTo(formScrollContainer, formScrollTop)
    })
  }

  releaseAnchoringSnapshot = snapshot => {
    if (!snapshot) return

    snapshot.anchoredContainers.forEach(element => {
      this.unlockOverflowAnchoring(element)
    })
  }

  restoreAnchoredSnapshot = snapshot => {
    if (!snapshot) return

    const anchor = document.getElementById(snapshot.anchorId)
    if (!anchor) return

    const { scrollContainer, anchorTopOffset } = snapshot
    const containerRect = scrollContainer.getBoundingClientRect()
    const anchorRect = anchor.getBoundingClientRect()
    const delta = (anchorRect.top - containerRect.top) - anchorTopOffset

    scrollContainer.scrollTop += delta
  }

  releaseStreamSnapshot = () => {
    if (!this.streamSnapshot) return

    this.unbindStreamIntentListeners()
    this.releaseAnchoringSnapshot(this.streamSnapshot)
    this.streamSnapshot = null
  }

  cancelStreamRestore = () => {
    this.streamRestoreVersion += 1
    this.cancelFrame(this.streamRestoreFrame)
    this.streamRestoreFrame = null
  }

  cancelAndReleaseStreamSnapshot = () => {
    this.cancelStreamRestore()
    this.releaseStreamSnapshot()
  }

  handleStreamContainerScroll = () => {
    if (!this.isRestoringStreamScroll) {
      this.lastStreamScrollAt = performance.now()
      this.markStreamUserIntent()
    }

    if (!this.streamSnapshot) return
    if (this.isRestoringStreamScroll) return

    this.cancelAndReleaseStreamSnapshot()
  }

  handleStreamUserIntent = () => {
    this.markStreamUserIntent()

    if (!this.streamSnapshot) return
    if (this.isRestoringStreamScroll) return

    this.cancelAndReleaseStreamSnapshot()
  }

  scheduleStreamRestore = () => {
    if (!this.streamSnapshot) return

    this.cancelStreamRestore()
    const restoreVersion = this.streamRestoreVersion

    this.streamRestoreFrame = requestAnimationFrame(() => {
      if (!this.snapshotIsTrustworthyForFollowUp(restoreVersion)) return

      this.restoreStreamSnapshot()
      this.releaseStreamSnapshot()
      this.streamRestoreFrame = null
    })
  }

  // Remove once https://linear.app/monami/issue/MON-3643/fix-super-form-with-field-notes-to-become-valid-html is solved
  isOwnElement = (el) => this.element.contains(el) && el.closest('form') === this.element

  preserveStreamScrollPosition = event => {
    const streamTargetId = event.target.target
    const streamTargetEl = streamTargetId && document.getElementById(streamTargetId)
    if (streamTargetEl && !this.isOwnElement(streamTargetEl)) return

    const snapshotIsStale =
      this.streamSnapshot &&
      this.lastStreamScrollAt > this.streamSnapshotCapturedAt

    if (snapshotIsStale) {
      this.cancelStreamRestore()
      this.releaseStreamSnapshot()
    }

    if (!this.streamSnapshot) {
      this.streamSnapshot = this.captureStreamSnapshot()

      if (this.streamSnapshot) {
        this.bindStreamIntentListeners()
      }
    }

    if (!this.streamSnapshot) return

    const originalRender = event.detail.render

    event.detail.render = async streamElement => {
      await originalRender(streamElement)

      if (!this.streamSnapshot) return

      this.restoreStreamSnapshot()
      this.scheduleStreamRestore()
    }
  }

  isVisibleWithinContainer = (rect, containerRect) => {
    return rect.height > 0 && rect.bottom > containerRect.top && rect.top < containerRect.bottom
  }

  findVisibleFieldAnchor = scrollContainer => {
    const formBody = this.element.querySelector('.superform-form-body')
    if (!formBody) return null

    const containerRect = scrollContainer.getBoundingClientRect()
    const containerCenter = containerRect.top + (containerRect.height / 2)

    const candidates = Array.from(formBody.querySelectorAll('[id^="document_"]'))
      .map(element => ({
        element,
        rect: element.getBoundingClientRect()
      }))
      .filter(({ rect }) => this.isVisibleWithinContainer(rect, containerRect))

    if (candidates.length === 0) return null

    const anchor = candidates.reduce((best, candidate) => {
      const bestDistance = Math.abs(((best.rect.top + best.rect.bottom) / 2) - containerCenter)
      const candidateDistance = Math.abs(((candidate.rect.top + candidate.rect.bottom) / 2) - containerCenter)

      return candidateDistance < bestDistance ? candidate : best
    })

    return {
      id: anchor.element.id,
      topOffset: anchor.rect.top - containerRect.top
    }
  }

  captureResizeSnapshot = () => {
    const scrollContainer = this.getActiveResizeScrollContainer()
    if (!scrollContainer) return null

    const anchor = this.findVisibleFieldAnchor(scrollContainer)
    if (!anchor) return null

    return {
      scrollContainer,
      anchorId: anchor.id,
      anchorTopOffset: anchor.topOffset,
      anchoredContainers: this.createAnchoringSnapshot([scrollContainer])
    }
  }

  restoreResizeSnapshot = () => {
    this.restoreAnchoredSnapshot(this.resizeSnapshot)
  }

  releaseResizeSnapshot = () => {
    if (!this.resizeSnapshot) return

    this.releaseAnchoringSnapshot(this.resizeSnapshot)
    this.resizeSnapshot = null
  }

  releaseSidebarSnapshot = () => {
    if (!this.sidebarSnapshot) return

    this.releaseAnchoringSnapshot(this.sidebarSnapshot)
    this.sidebarSnapshot = null
  }

  scheduleResizeRestore = () => {
    if (!this.resizeSnapshot) return

    this.cancelFrame(this.resizeRestoreFrame)
    this.resizeRestoreFrame = requestAnimationFrame(() => {
      this.restoreResizeSnapshot()
      this.resizeRestoreFrame = null
    })
  }

  settleResize = () => {
    if (!this.resizeSnapshot) return

    this.cancelFrame(this.resizeReleaseFrame)
    this.resizeReleaseFrame = requestAnimationFrame(() => {
      this.restoreResizeSnapshot()

      this.resizeReleaseFrame = requestAnimationFrame(() => {
        this.restoreResizeSnapshot()
        this.releaseResizeSnapshot()
        this.resizeReleaseFrame = null
      })
    })
  }

  parseTransitionTimeToMs = value => {
    const trimmed = value?.trim()
    if (!trimmed) return 0
    if (trimmed.endsWith('ms')) return Number.parseFloat(trimmed) || 0
    if (trimmed.endsWith('s')) return (Number.parseFloat(trimmed) || 0) * 1000

    return Number.parseFloat(trimmed) || 0
  }

  getSidebarTransitionDurationMs = sidebar => {
    if (!sidebar) return 0

    const styles = window.getComputedStyle(sidebar)
    const durations = styles.transitionDuration.split(',').map(this.parseTransitionTimeToMs)
    const delays = styles.transitionDelay.split(',').map(this.parseTransitionTimeToMs)

    return durations.reduce((maxDuration, duration, index) => {
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0
      return Math.max(maxDuration, duration + delay)
    }, 0)
  }

  teardownSidebarTransitionListener = () => {
    if (this.sidebarTransitionElement && this.sidebarTransitionHandler) {
      this.sidebarTransitionElement.removeEventListener('transitionend', this.sidebarTransitionHandler)
    }

    this.sidebarTransitionElement = null
    this.sidebarTransitionHandler = null
    clearTimeout(this.sidebarTransitionTimeout)
    this.sidebarTransitionTimeout = null
  }

  cancelSidebarRestore = () => {
    this.cancelFrame(this.sidebarRestoreFrame)
    this.cancelFrame(this.sidebarReleaseFrame)
    this.sidebarRestoreFrame = null
    this.sidebarReleaseFrame = null
    this.teardownSidebarTransitionListener()
  }

  cancelAndReleaseSidebarSnapshot = () => {
    this.cancelSidebarRestore()
    this.releaseSidebarSnapshot()
  }

  scheduleSidebarRestore = () => {
    if (!this.sidebarSnapshot) return

    this.cancelFrame(this.sidebarRestoreFrame)

    const restore = () => {
      if (!this.sidebarSnapshot) return

      this.restoreAnchoredSnapshot(this.sidebarSnapshot)
      this.sidebarRestoreFrame = requestAnimationFrame(restore)
    }

    this.sidebarRestoreFrame = requestAnimationFrame(restore)
  }

  settleSidebarRestore = () => {
    if (!this.sidebarSnapshot) return

    this.cancelFrame(this.sidebarRestoreFrame)
    this.sidebarRestoreFrame = null
    this.teardownSidebarTransitionListener()

    this.cancelFrame(this.sidebarReleaseFrame)
    this.sidebarReleaseFrame = requestAnimationFrame(() => {
      this.restoreAnchoredSnapshot(this.sidebarSnapshot)

      this.sidebarReleaseFrame = requestAnimationFrame(() => {
        this.restoreAnchoredSnapshot(this.sidebarSnapshot)
        this.releaseSidebarSnapshot()
        this.sidebarReleaseFrame = null
      })
    })
  }

  watchSidebarTransition = sidebar => {
    const transitionDurationMs = this.getSidebarTransitionDurationMs(sidebar)
    if (transitionDurationMs <= 0) {
      this.settleSidebarRestore()
      return
    }

    const handleTransitionEnd = event => {
      if (event.target === sidebar && event.propertyName === 'width') {
        this.settleSidebarRestore()
      }
    }

    this.sidebarTransitionElement = sidebar
    this.sidebarTransitionHandler = handleTransitionEnd
    sidebar.addEventListener('transitionend', handleTransitionEnd)
    this.sidebarTransitionTimeout = setTimeout(() => {
      this.settleSidebarRestore()
    }, transitionDurationMs + SIDEBAR_TRANSITION_BUFFER_MS)
  }

  handleSidebarLayoutChange = event => {
    const sidebar = this.getSidebarElement(event)
    if (!sidebar) return

    this.cancelAndReleaseSidebarSnapshot()
    this.sidebarSnapshot = this.captureResizeSnapshot()
    if (!this.sidebarSnapshot) return

    this.scheduleSidebarRestore()
    this.watchSidebarTransition(sidebar)
  }

  handleResize = () => {
    if (!this.resizeSnapshot) {
      this.resizeSnapshot = this.captureResizeSnapshot()
    }
    if (!this.resizeSnapshot) return

    this.scheduleResizeRestore()

    clearTimeout(this.resizeSettleTimer)
    this.resizeSettleTimer = setTimeout(() => {
      this.settleResize()
      this.resizeSettleTimer = null
    }, RESIZE_SETTLE_DELAY_MS)
  }
}
